// views/Home/HomeDashboard.jsx
//
// Workspace overview shown at /home (the house icon in the app rail). Pulls real
// data — projects, agents, workflows, connections and coin balance — and lays it
// out as a scrollable dashboard: hero banner, stat cards, continue-working,
// quick actions, recent projects, needs-attention and a setup checklist.
import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Headset,
  Workflow,
  FolderKanban,
  Coins,
  ArrowRight,
  Puzzle,
  FolderPlus,
  AlertTriangle,
  Info,
  MinusCircle,
  CheckCircle2,
  Circle,
  Clock,
  MoreVertical,
  ChevronRight,
} from "lucide-react";
import AuthContext from "../../AuthContext";
import { useOrganizations, useProjects } from "../../hooks/useOrganizations";
import { useAccountBalance } from "../../hooks/billing/useAccountBalance";
import AssistantService from "../../services/assistant.service";
import WorkflowService from "../../services/workflow.service";
import IntegrationService from "../../services/integration.service";
import { getProjectBackground } from "../../assets/projects";
import homebanner from "../../assets/homebanner.jpeg";
import FadeImg from "../../components/ui/FadeImg";
import MobileTopBar from "../../components/Sidebar/MobileTopBar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useCommandPalette } from "../../components/CommandPalette/CommandPaletteContext";

const LOW_COIN_THRESHOLD = 2000;

const timeAgo = (dateString) => {
  if (!dateString) return null;
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const recentBy = (list, n = 3) =>
  [...(Array.isArray(list) ? list : [])]
    .sort(
      (a, b) =>
        new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0) -
        new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0)
    )
    .slice(0, n);

const statusDot = (status) => {
  const s = (status || "active").toLowerCase();
  if (s === "active") return "text-emerald-400";
  if (s === "paused") return "text-amber-400";
  return "text-gray-400";
};

export default function HomeDashboard() {
  const navigate = useNavigate();
  const { openPalette } = useCommandPalette();
  const { user } = useContext(AuthContext);
  const { currentAccount } = useOrganizations();
  const { projects, loading: projectsLoading } = useProjects();
  const accountId = currentAccount?.id;
  // El dinero vive en la cuenta, así que su URL lleva el id. Antes esto iba a
  // /user/wallet, que sigue funcionando por redirección — pero es un salto de
  // más, con su parpadeo de "cargando" antes de aterrizar.
  const goToBilling = useCallback(
    () => navigate(accountId ? `/a/${accountId}/billing` : '/user/wallet'),
    [navigate, accountId]
  );
  // On a hard refresh the account is first restored from localStorage marked
  // `_restored` (not yet verified against the user). Wait for a confirmed
  // account before fetching, and refetch once it's confirmed — otherwise the
  // requests fire against a half-restored context and Home ends up empty.
  const accountReady = !!accountId && !currentAccount?._restored;
  const { data: balance } = useAccountBalance(accountReady ? accountId : null);

  const [assistants, setAssistants] = useState([]);
  const [workflows, setWorkflows] = useState([]); // flattened across projects
  const [projectWfCount, setProjectWfCount] = useState({}); // { [projectId]: n }
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [workflowsLoading, setWorkflowsLoading] = useState(true);

  const projectList = useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);
  const projectIdsKey = projectList.map((p) => p.id).join(",");

  // Agents + connections: one call each, account-scoped.
  useEffect(() => {
    if (!accountReady) return;
    let cancelled = false;
    AssistantService.listAssistants({ accountId, limit: 100 })
      .then((res) => {
        if (!cancelled && res?.success) setAssistants(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setAgentsLoading(false);
      });
    IntegrationService.listAllIntegrations(accountId)
      .then((res) => {
        if (cancelled || !res?.success) return;
        const d = res.data || {};
        setConnectionsCount((d.meta?.length || 0) + (d.shopify?.length || 0) + (d.google?.length || 0));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accountReady, accountId]);

  // Workflows: the endpoint requires a project_id, so fetch per project and merge.
  useEffect(() => {
    if (!accountReady) return;
    if (projectList.length === 0) {
      setWorkflows([]);
      setProjectWfCount({});
      setWorkflowsLoading(false);
      return;
    }
    let cancelled = false;
    setWorkflowsLoading(true);
    Promise.all(
      projectList.slice(0, 12).map((p) =>
        WorkflowService.listWorkflows({
          account_id: accountId,
          project_id: p.id,
          limit: 50,
          include_definition: false,
        })
          .then((r) => (r?.success ? (r.data?.workflows || []).map((w) => ({ ...w, project_id: p.id })) : []))
          .catch(() => [])
      )
    ).then((perProject) => {
      if (cancelled) return;
      const counts = {};
      perProject.forEach((wfs, i) => {
        counts[projectList[i]?.id] = wfs.length;
      });
      setProjectWfCount(counts);
      setWorkflows(perProject.flat());
      setWorkflowsLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountReady, accountId, projectIdsKey]);

  // One flag so the "Continue working" cards appear together, not one by one.
  const continueLoading = projectsLoading || agentsLoading || workflowsLoading;

  const coins = balance?.totalBalance;
  const agentsTotal = assistants.length;
  const workflowsTotal = workflows.length;

  const recentProjects = useMemo(() => recentBy(projectList), [projectIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const recentWorkflows = useMemo(() => recentBy(workflows), [workflows]);

  // Match how the Profile shows the user: username first, then name. Take the
  // first word so the greeting stays short.
  const firstName = (user?.username || user?.name || user?.given_name || user?.first_name || "")
    .toString()
    .trim()
    .split(" ")[0];

  // Continue working: newest of each kind (project, agent, workflow) that exists.
  const continueItems = useMemo(() => {
    const items = [];
    const proj = recentProjects[0];
    if (proj) {
      items.push({
        key: "project",
        type: "Project",
        name: proj.name,
        sub: `${projectWfCount[proj.id] ?? 0} workflows`,
        status: proj.status,
        updated: proj.updated_at || proj.created_at,
        image: getProjectBackground(0),
        onClick: () => navigate(`/dashboard?view=Projects&projectId=${proj.project_id || proj.id}`),
      });
    }
    // Only surface an agent that actually belongs to a project in the current
    // context — otherwise clicking it opens Projects with nothing there (the
    // agent lives in another account/org, or has no project).
    const projectIds = new Set(projectList.map((p) => p.id));
    const agent = recentBy(
      assistants.filter((a) => projectIds.has(a.projectId || a.project_id))
    )[0];
    if (agent) {
      const agentProjectId = agent.projectId || agent.project_id;
      items.push({
        key: "agent",
        type: "Agent",
        name: agent.name || "Untitled agent",
        sub: agent.description || "AI agent",
        avatar: agent.avatar,
        updated: agent.updated_at || agent.created_at,
        image: getProjectBackground(3),
        onClick: () => navigate(`/dashboard?view=Projects&projectId=${agentProjectId}&tab=assistants`),
      });
    }
    const wf = recentWorkflows[0];
    if (wf) {
      const wfProjectId = wf.project_id || wf.projectId;
      items.push({
        key: "workflow",
        type: "Workflow",
        name: wf.name || "Untitled workflow",
        sub: wf.status ? String(wf.status) : "Workflow",
        updated: wf.updated_at || wf.created_at,
        image: getProjectBackground(1),
        onClick: () =>
          navigate(
            wfProjectId
              ? `/dashboard?view=Projects&projectId=${wfProjectId}&tab=workflows`
              : "/dashboard?view=Projects"
          ),
      });
    }
    return items;
  }, [recentProjects, assistants, recentWorkflows, projectWfCount, projectList, navigate]);

  const stats = [
    { icon: FolderKanban, value: projectList.length, label: "Projects", cta: "View all", accent: "text-pink-400", ring: "bg-pink-500/10", onClick: () => navigate("/dashboard?view=Projects") },
    // "View all" llevaba a Projects en las tres tarjetas. Tenia sentido cuando
    // agentes y workflows solo se alcanzaban por dentro de un proyecto; ahora
    // que son destinos propios, cada una va a lo suyo.
    { icon: Headset,value: agentsTotal, label: "Agents", cta: "View all", accent: "text-purple-400", ring: "bg-purple-500/10", onClick: () => navigate("/dashboard?view=Agents") },
    { icon: Workflow, value: workflowsTotal, label: "Workflows", cta: "View all", accent: "text-blue-400", ring: "bg-blue-500/10", onClick: () => navigate("/dashboard?view=Workflows") },
    { icon: Coins, value: coins != null ? coins.toLocaleString("en-US") : "—", label: "Coins available", cta: "Buy more", accent: "text-amber-400", ring: "bg-amber-500/10", onClick: () => goToBilling() },
  ];

  // Solo lo que no esta ya en la pantalla. "Create agent" y "Create workflow"
  // estaban aqui y tambien en el hero, a 600px de distancia y en el mismo
  // viewport; "Buy coins" repetia el "Buy more" de la tarjeta de monedas, con
  // otro nombre. Ver el mismo boton dos veces no se lee como comodidad, se lee
  // como "¿seran lo mismo?" — y esa duda es la que hace que uno se pierda.
  const quickActions = [
    { icon: Puzzle, title: "Connect integration", desc: "Connect external apps and services", accent: "text-blue-300", ring: "border-blue-500/30 bg-blue-500/10", img: 5, onClick: () => navigate("/dashboard?view=Integrations") },
    { icon: FolderPlus, title: "Create project", desc: "Organize your agents and workflows", accent: "text-emerald-300", ring: "border-emerald-500/30 bg-emerald-500/10", img: 2, onClick: () => navigate("/dashboard?view=Projects") },
  ];

  // Needs attention: real, derived signals.
  const attention = useMemo(() => {
    const list = [];
    if (coins != null && coins < LOW_COIN_THRESHOLD) {
      list.push({ icon: AlertTriangle, tone: "text-amber-400", title: "Low coin balance", desc: `You have ${coins.toLocaleString("en-US")} coins left. Consider topping up.`, onClick: () => goToBilling() });
    }
    const paused = projectList.filter((p) => (p.status || "").toLowerCase() === "paused").length;
    if (paused > 0) {
      list.push({ icon: Info, tone: "text-blue-400", title: `${paused} paused ${paused === 1 ? "project" : "projects"}`, desc: "Resume them to keep automations running.", onClick: () => navigate("/dashboard?view=Projects") });
    }
    const noWf = projectList.filter((p) => (projectWfCount[p.id] ?? 0) === 0).length;
    if (noWf > 0) {
      list.push({ icon: MinusCircle, tone: "text-gray-400", title: `${noWf} ${noWf === 1 ? "project has" : "projects have"} no workflows`, desc: "Add a workflow to start automating.", onClick: () => navigate("/dashboard?view=Projects") });
    }
    return list;
  }, [coins, projectList, projectWfCount, navigate, goToBilling]);

  // Setup checklist — progress derives from real state.
  const setup = [
    { label: "Create your first project", done: projectList.length > 0, onClick: () => navigate("/dashboard?view=Projects") },
    { label: "Add your first agent", done: agentsTotal > 0, onClick: () => navigate("/dashboard?view=Projects") },
    { label: "Connect an integration", done: connectionsCount > 0, onClick: () => navigate("/dashboard?view=Integrations") },
    { label: "Create your first workflow", done: workflowsTotal > 0, onClick: () => navigate("/dashboard?view=Projects") },
    { label: "Buy coins or choose a plan", done: (balance?.extraBalance || 0) > 0 || (balance?.tier?.type && balance.tier.type !== "FREE"), onClick: () => goToBilling() },
  ];
  const setupDone = setup.filter((s) => s.done).length;
  // El checklist y Quick actions son las mismas acciones para dos publicos
  // distintos: mientras falta setup, el checklist gana porque ademas lleva el
  // progreso; una vez completo deja de aportar y le cede el sitio. Antes se
  // veian los dos a la vez, y el checklist seguia ahi diciendo "5 of 5
  // completed" para siempre.
  const setupComplete = setupDone === setup.length;

  return (
    <div className="h-screen bg-black relative overflow-hidden">
      {/* Shared dashboard ambience: the same purple radial glow every other
          /dashboard view carries, so Home stops looking like a flat-black
          outlier. Mirrors AIDashboard's blob (top-left, blurred). */}
      <div
        className="pointer-events-none absolute rounded-full"
        style={{
          top: "1rem",
          left: "4rem",
          width: "33rem",
          height: "33rem",
          background:
            "radial-gradient(circle at 25% 25%, rgba(75,0,130,0.8) 0%, rgba(75,0,130,0.4) 50%, transparent 90%)",
          filter: "blur(140px)",
        }}
      />
      <main className="relative z-10 h-full overflow-y-auto rail-pad">
        {/* El "+" repite las tres creaciones que ya viven en esta pantalla —el
            hero crea agente y workflow, Quick actions crea proyecto—, pero
            desde arriba y con el pulgar cerca: en móvil el hero se va de
            pantalla en cuanto bajas. */}
        <MobileTopBar
          action={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Create"
                  className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-r from-[#7c3aed] to-[#a147e0] text-white shadow-lg shadow-purple-500/25 transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              {/* El resaltado va explícito. `DropdownMenuItem` lo trae con
                  `focus:bg-accent`, y `accent` es otra de las variables que
                  están declaradas en input.css pero no mapeadas en el config:
                  la clase no se genera, así que los elementos no se iluminan al
                  tocarlos. Aquí se resuelve como en el menú del rail. */}
              <DropdownMenuContent
                align="end"
                className="min-w-[12rem] border-white/10 bg-popover text-popover-foreground"
              >
                <DropdownMenuItem
                  className="cursor-pointer focus:bg-white/10 hover:bg-white/10"
                  onSelect={() => navigate("/dashboard?view=Projects")}
                >
                  <Headset className="mr-2 h-4 w-4" /> Create agent
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer focus:bg-white/10 hover:bg-white/10"
                  onSelect={() => navigate("/dashboard?view=Projects")}
                >
                  <Workflow className="mr-2 h-4 w-4" /> Create workflow
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer focus:bg-white/10 hover:bg-white/10"
                  onSelect={() => navigate("/dashboard?view=Projects")}
                >
                  <FolderPlus className="mr-2 h-4 w-4" /> Create project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />

        <div className="w-full px-4 py-5 sm:px-6 lg:px-8 2xl:px-10">
          {/* Aquí había un segundo buscador, con el mismo texto y abriendo el
              mismo ⌘K que el del menú, que está siempre a la vista y a dos
              centímetros. Dos campos idénticos en pantalla no son dos caminos:
              son la duda de si buscan cosas distintas.

              Ese argumento vale mientras el menú esté a la vista, y en móvil no
              lo está: el rail se oculta por debajo de `md`. Por eso la búsqueda
              vive ahora en `MobileTopBar`, que es el rail de móvil — no aquí,
              donde volvería a ser un segundo campo. */}

          {/* Hero banner */}
          <div className="relative mb-6 overflow-hidden rounded-2xl border border-white/10 sm:rounded-3xl">
            <FadeImg src={homebanner} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
            <div className="relative z-10 max-w-xl px-6 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-11">
              <p className="mb-2 text-sm font-medium text-[#c9a9ff]">
                Welcome back{firstName ? `, ${firstName}` : ""}!
              </p>
              <h1 className="mb-2.5 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                Build, automate and<br className="hidden sm:block" /> scale with AI
              </h1>
              <p className="mb-6 max-w-md text-sm leading-relaxed text-white/70 lg:text-base">
                Manage your projects, agents, workflows and integrations all from one place.
              </p>
              {/* Las dos llevaban a la lista de proyectos, que es el sitio
                  correcto y ninguna acción. Un agente y un workflow viven
                  dentro de un proyecto, así que lo primero es cuál — y eso ya
                  lo pregunta el ⌘K, que es la misma acción con el mismo paso.
                  Una sola implementación para las dos entradas.

                  Apilados y a lo ancho en móvil: uno al lado del otro salen a
                  ~150px cada uno, más estrecho que el pulgar que los pulsa. */}
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button
                  onClick={() => openPalette({ page: "new-agent" })}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#a147e0] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-500/25 transition-all hover:scale-105 sm:w-auto sm:py-2.5"
                >
                  <Plus className="h-4 w-4" /> Create agent
                </button>
                <button
                  onClick={() => openPalette({ page: "new-workflow" })}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/[0.06] px-5 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/10 sm:w-auto sm:py-2.5"
                >
                  <Workflow className="h-4 w-4" /> Create workflow
                </button>
              </div>
            </div>
          </div>

          {/* Stat cards */}
          <div className="mb-7 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {stats.map((s) => (
              <button
                key={s.label}
                onClick={s.onClick}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-left transition-colors hover:border-white/20 sm:gap-4 sm:px-5 sm:py-4"
              >
                <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${s.ring}`}>
                  <s.icon className={`h-6 w-6 ${s.accent}`} />
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold leading-none tracking-tight text-white">{s.value}</div>
                  <div className="mt-1 truncate text-sm text-white/70">{s.label}</div>
                  <div className="mt-1 flex items-center gap-1 text-xs font-medium text-[#b98bff]">
                    {s.cta} <ArrowRight className="h-3 w-3" />
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Continue working */}
          {(continueLoading || continueItems.length > 0) && (
            <section className="mb-8">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Continue working</h2>
                <button onClick={() => navigate("/dashboard?view=Projects")} className="flex items-center gap-1 text-xs font-medium text-[#b98bff] hover:text-purple-300">
                  View all <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              {/* En móvil la tarjeta se tumba: la miniatura a la izquierda y el
                  texto al lado. Apilada, cada una se lleva media pantalla de
                  alto y sólo caben dos —de una lista cuyo sentido es ver de un
                  vistazo en qué estabas. */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
                {continueLoading
                  ? [0, 1, 2].map((i) => <CardSkeleton key={i} />)
                  : continueItems.map((it) => {
                  const updated = timeAgo(it.updated);
                  const updatedLabel = updated ? `Updated ${updated}` : "—";
                  return (
                  <button
                    key={it.key}
                    onClick={it.onClick}
                    className="group flex animate-fade-in overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] text-left transition-colors hover:border-purple-500/40 md:block"
                  >
                    <div className="relative w-28 shrink-0 overflow-hidden md:h-28 md:w-full">
                      <FadeImg src={it.image} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                      <span className="absolute left-2 top-2 rounded-full border border-white/15 bg-black/50 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm md:left-3 md:top-3 md:px-2.5">
                        {it.type}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 items-center gap-2 p-4 md:block">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2.5">
                          {it.avatar ? (
                            <img src={it.avatar} alt="" className="h-8 w-8 flex-shrink-0 rounded-lg object-cover" />
                          ) : null}
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-white">{it.name}</div>
                            <div className="flex items-center gap-1.5 truncate text-xs text-white/50">
                              {it.status ? <span className={statusDot(it.status)}>●</span> : null}
                              {it.sub}
                            </div>
                          </div>
                        </div>
                        {/* La fecha sube a la columna de texto y se queda sin
                            separador: tumbada, la línea de arriba ya no cruza
                            la tarjeta entera y el filete quedaba flotando. */}
                        <span className="mt-1.5 flex items-center gap-1.5 text-[11px] text-white/40 md:hidden">
                          <Clock className="h-3 w-3" /> {updatedLabel}
                        </span>
                      </div>
                      <ChevronRight className="h-5 w-5 shrink-0 text-white/30 md:hidden" />
                      <div className="mt-3 hidden items-center justify-between border-t border-white/5 pt-3 md:flex">
                        <span className="flex items-center gap-1.5 text-[11px] text-white/40">
                          <Clock className="h-3 w-3" /> {updatedLabel}
                        </span>
                        <ArrowRight className="h-4 w-4 text-white/40 transition-transform group-hover:translate-x-1 group-hover:text-[#b98bff]" />
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Quick actions — solo cuando el setup ya no tiene nada que decir */}
          {setupComplete && (
          <section className="mb-8">
            <h2 className="mb-4 text-lg font-bold text-white">Quick actions</h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:max-w-2xl">
              {quickActions.map((a) => (
                <button
                  key={a.title}
                  onClick={a.onClick}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-purple-500/40 hover:scale-[1.02]"
                >
                  <img src={getProjectBackground(a.img)} alt="" className="pointer-events-none absolute inset-x-0 bottom-0 h-16 w-full object-cover opacity-20 transition-opacity group-hover:opacity-30" />
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-transparent to-[#0b0510]" />
                  <div className="relative z-10">
                    <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-full border ${a.ring}`}>
                      <a.icon className={`h-5 w-5 ${a.accent}`} />
                    </div>
                    <div className="text-sm font-semibold text-white">{a.title}</div>
                    <div className="mt-1 text-xs leading-snug text-white/50">{a.desc}</div>
                    <ArrowRight className="mt-3 h-4 w-4 text-white/40 transition-transform group-hover:translate-x-1 group-hover:text-[#b98bff]" />
                  </div>
                </button>
              ))}
            </div>
          </section>
          )}

          {/* Recent projects */}
          {recentProjects.length > 0 && (
            <section className="mb-8">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Recent projects</h2>
                <button onClick={() => navigate("/dashboard?view=Projects")} className="flex items-center gap-1 text-xs font-medium text-[#b98bff] hover:text-purple-300">
                  View all projects <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              {/* Mismo trato que "Continue working": son la misma clase de
                  tarjeta y tienen que leerse igual una debajo de la otra. */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-5 xl:grid-cols-3">
                {recentProjects.map((p, i) => {
                  const updated = timeAgo(p.updated_at || p.created_at);
                  const updatedLabel = updated ? `Updated ${updated}` : "—";
                  return (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/dashboard?view=Projects&projectId=${p.project_id || p.id}`)}
                    className="group flex overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] text-left transition-colors hover:border-purple-500/40 md:block"
                  >
                    <div className="relative w-28 shrink-0 overflow-hidden md:h-32 md:w-full">
                      <FadeImg src={getProjectBackground(i)} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                      {/* Sobre una miniatura de 112px no cabe: en móvil el menú
                          se pide desde el propio proyecto. */}
                      <button className="absolute right-2.5 top-2.5 hidden rounded-lg bg-black/40 p-1.5 text-white/70 backdrop-blur-sm transition-colors hover:text-white md:block" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex min-w-0 flex-1 items-center gap-2 p-4 md:block">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white line-clamp-1 md:text-base">{p.name}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-white/50">
                          <span>{assistants.filter((a) => a.projectId === p.id).length} agents</span>
                          <span>·</span>
                          <span>{projectWfCount[p.id] ?? 0} workflows</span>
                          <span>·</span>
                          <span className="flex items-center gap-1 capitalize">
                            <span className={statusDot(p.status)}>●</span> {p.status || "active"}
                          </span>
                        </div>
                        <span className="mt-1.5 flex items-center gap-1.5 text-[11px] text-white/40 md:hidden">
                          <Clock className="h-3 w-3" /> {updatedLabel}
                        </span>
                      </div>
                      <ChevronRight className="h-5 w-5 shrink-0 text-white/30 md:hidden" />
                      <div className="mt-3 hidden items-center justify-between border-t border-white/5 pt-3 md:flex">
                        <span className="flex items-center gap-1.5 text-[11px] text-white/40">
                          <Clock className="h-3 w-3" /> {updatedLabel}
                        </span>
                        <ArrowRight className="h-4 w-4 text-white/40 transition-transform group-hover:translate-x-1 group-hover:text-[#b98bff]" />
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Needs attention + Setup checklist */}
          <section className="mb-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {/* Needs attention */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="mb-4 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <h3 className="text-base font-bold text-white">Needs attention</h3>
              </div>
              {attention.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <CheckCircle2 className="mb-2 h-8 w-8 text-emerald-400/70" />
                  <p className="text-sm text-white/50">You&apos;re all caught up.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {attention.map((a, i) => (
                    <button key={i} onClick={a.onClick} className="flex w-full items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-left transition-colors hover:border-white/20">
                      <a.icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${a.tone}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white">{a.title}</div>
                        <div className="text-xs text-white/50">{a.desc}</div>
                      </div>
                      <ArrowRight className="mt-1 h-4 w-4 flex-shrink-0 text-white/30" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Complete your setup — desaparece al completarse: un checklist
                al 100% no le pide nada a nadie y solo ocupa la portada. */}
            {!setupComplete && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-bold text-white">Complete your setup</h3>
                <span className="text-xs font-medium text-white/50">{setupDone} of {setup.length} completed</span>
              </div>
              <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-gradient-to-r from-[#7c3aed] to-[#e0459b] transition-all" style={{ width: `${(setupDone / setup.length) * 100}%` }} />
              </div>
              <div className="space-y-1">
                {setup.map((s) => (
                  <button key={s.label} onClick={s.onClick} className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-white/[0.04]">
                    {s.done ? (
                      <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-400" />
                    ) : (
                      <Circle className="h-5 w-5 flex-shrink-0 text-white/30" />
                    )}
                    <span className={`flex-1 text-sm ${s.done ? "text-white/50 line-through" : "text-white/90"}`}>{s.label}</span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-white/30" />
                  </button>
                ))}
              </div>
            </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

// Placeholder card shown while continue-working / recent-projects data loads,
// so the whole row appears at once instead of popping in item by item.
// Se tumba en móvil igual que la tarjeta que sustituye: si el hueco no tiene
// la forma de lo que va a llegar, la lista salta al cargar.
function CardSkeleton() {
  return (
    <div className="flex overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] md:block">
      {/* Las clases van literales: Tailwind lee el código fuente, así que una
          armada con `${...}` en tiempo de ejecución no existe en output.css. */}
      <div className="w-28 shrink-0 skeleton !rounded-none md:h-28 md:w-full" />
      <div className="flex-1 space-y-3 p-4">
        <div className="h-4 w-1/2 skeleton" />
        <div className="h-3 w-3/4 skeleton" />
        <div className="mt-3 border-t border-white/5 pt-3">
          <div className="h-3 w-1/3 skeleton" />
        </div>
      </div>
    </div>
  );
}
